import { useState } from 'react';
import PropTypes from 'prop-types';
import ReactSelect from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { HelpCircle } from 'lucide-react';

import { cn } from '#/lib/utils';

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '#/modules/commonComponents/tooltip';

import { getNestedError } from '../formFieldUtils';

import { buildSelectStyles, mergeSelectStyles, selectTheme } from './selectStyles';

const defaultSelectClassNames = {
  valueContainer: () => 'px-0',
  singleValue: () => 'text-base text-gray-700 md:text-sm',
  input: () => 'text-base text-gray-700 md:text-sm',
  placeholder: () => 'text-base md:text-sm',
  multiValueLabel: () => 'text-base text-gray-700 md:text-sm',
};

function mergeSelectClassNames(overrides) {
  if (!overrides) {
    return defaultSelectClassNames;
  }

  const merged = { ...defaultSelectClassNames };

  Object.keys(overrides).forEach((key) => {
    const overrideFn = overrides[key];
    const defaultFn = defaultSelectClassNames[key];

    merged[key] = (state) => cn(defaultFn?.(state), overrideFn?.(state));
  });

  return merged;
}

function resolveSelectValue(value, options, isMulti) {
  if (isMulti) {
    if (!Array.isArray(value) || value.length === 0) {
      return [];
    }
    if (typeof value[0] === 'object' && value[0] !== null) {
      return value;
    }
    return options.filter((option) => value.includes(option.value));
  }
  if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
    return value;
  }
  return options.find((option) => option.value === value) || null;
}

function extractChangeValue(selected, isMulti) {
  if (isMulti) {
    return Array.isArray(selected) ? selected.map((option) => option.value) : [];
  }
  return selected?.value ?? '';
}

const SelectCore = ({
  id,
  label,
  tip,
  options,
  value,
  onChange,
  isMulti = false,
  isDisabled = false,
  creatable = false,
  errors,
  placeholder = '請選擇',
  className = '',
  classNames,
  containerClassName = '',
  noOptionsMessage = '無其他選項',
  hideErrorMessage = false,
  onBlur,
  styles: stylesProp,
  ...props
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const fieldError = errors ? getNestedError(errors, id) : null;
  const reactSelectValue = resolveSelectValue(value, options, isMulti);
  const hasValue = isMulti
    ? (Array.isArray(reactSelectValue) && reactSelectValue.length > 0)
    : (reactSelectValue !== null && reactSelectValue !== undefined);
  const errorMessage = fieldError ? fieldError.message || null : null;
  const SelectComponent = creatable ? CreatableSelect : ReactSelect;
  const mergedStyles = mergeSelectStyles(
    buildSelectStyles(Boolean(fieldError)),
    stylesProp,
  );

  const selectElement = (
    <SelectComponent
      options={options}
      isMulti={isMulti}
      value={reactSelectValue}
      onChange={(selected) => onChange(extractChangeValue(selected, isMulti))}
      placeholder={label ? ' ' : placeholder}
      classNames={mergeSelectClassNames(classNames)}
      isDisabled={isDisabled}
      inputId={id}
      classNamePrefix="react-select"
      noOptionsMessage={() => noOptionsMessage}
      onMenuOpen={() => {
        setMenuIsOpen(true);
        setIsFocused(true);
      }}
      onMenuClose={() => {
        setMenuIsOpen(false);
        setIsFocused(false);
      }}
      onFocus={() => setIsFocused(true)}
      onBlur={() => {
        if (!menuIsOpen) {
          setIsFocused(false);
        }
        if (onBlur) {
          onBlur();
        }
      }}
      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
      menuPosition="fixed"
      menuShouldBlockScroll={false}
      {...props}
      menuPlacement="auto"
      minMenuHeight={240}
      styles={mergedStyles}
      theme={selectTheme}
    />
  );

  if (label) {
    const labelClassName = cn(
      'select_label',
      (isFocused || hasValue) && 'label_floating',
      isFocused && !fieldError && 'label_focused',
      hasValue && !isFocused && !fieldError && 'label_filled',
      fieldError && 'label_invalid',
      isDisabled && 'label_disabled',
    );

    return (
      <div className={cn('select_container', containerClassName, className)}>
        <div className="relative">
          {selectElement}
          <label htmlFor={id} className={labelClassName}>
            <span className="inline-flex items-center gap-1">
              <span>{label}</span>
              {tip ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <HelpCircle size={14} className="text-gray-500" />
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs">
                    {tip}
                  </TooltipContent>
                </Tooltip>
              ) : null}
            </span>
          </label>
        </div>
        {hideErrorMessage ? null : (
          <p className="form_error">{errorMessage}</p>
        )}
      </div>
    );
  }

  return (
    <>
      {selectElement}
      {!hideErrorMessage && errorMessage ? (
        <p className="form_error">{errorMessage}</p>
      ) : null}
    </>
  );
};

SelectCore.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string,
  tip: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.array,
    PropTypes.object,
  ]),
  onChange: PropTypes.func.isRequired,
  isMulti: PropTypes.bool,
  isDisabled: PropTypes.bool,
  creatable: PropTypes.bool,
  errors: PropTypes.object,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  classNames: PropTypes.object,
  containerClassName: PropTypes.string,
  noOptionsMessage: PropTypes.string,
  hideErrorMessage: PropTypes.bool,
  onBlur: PropTypes.func,
  styles: PropTypes.object,
};

export default SelectCore;
